// frontend/src/components/admin/DevisManager.jsx

import { useState, useEffect } from 'react';
import {
  Plus, Edit3, Eye, Send, CheckCircle2, XCircle,
  AlertCircle, RefreshCw, FileText, ArrowLeft, Save,
  ChevronDown, User, Building2, CreditCard, MessageCircle,
  ArrowRight, Clock, Calendar, Package, Layers, Users,
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import superAdminApi from '../../services/superAdminApi';
import { API_URL } from '../../config/apiBase';

// ============================================================================
// CONFIG STATUTS
// ============================================================================
const STATUT_CONFIG = {
  brouillon: { label: 'Brouillon', color: '#9ca3af', bg: '#f3f4f6',  icon: FileText    },
  envoye:    { label: 'Envoyé',    color: '#3b82f6', bg: '#eff6ff',  icon: Send        },
  accepte:   { label: 'Accepté',   color: '#22c55e', bg: '#f0fdf4',  icon: CheckCircle2},
  refuse:    { label: 'Refusé',    color: '#ef4444', bg: '#fef2f2',  icon: XCircle     },
  expire:    { label: 'Expiré',    color: '#f59e0b', bg: '#fffbeb',  icon: Clock       },
};

const LICENCES = ['basique', 'standard', 'premium'];

// ============================================================================
// SOUS-COMPOSANTS
// ============================================================================

const StatBadge = ({ statut }) => {
  const sc = STATUT_CONFIG[statut] || STATUT_CONFIG.brouillon;
  return (
    <span style={{ fontSize: 10, fontWeight: 700, padding: '3px 9px', borderRadius: 99,
      background: sc.bg, color: sc.color, whiteSpace: 'nowrap' }}>
      {sc.label}
    </span>
  );
};

const Field = ({ label, value, onChange, placeholder, type = 'text', theme: t, half }) => (
  <div style={{ gridColumn: half ? 'span 1' : 'span 2' }}>
    <label style={{ fontSize: 11, fontWeight: 700, color: t.textSecondary, display: 'block', marginBottom: 5 }}>
      {label}
    </label>
    <input type={type} value={value || ''} onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      style={{ width: '100%', padding: '8px 10px', borderRadius: 8, background: t.bgInput,
        border: `1px solid ${t.border}`, color: t.text, fontSize: 12, boxSizing: 'border-box', outline: 'none' }}
      onFocus={e => { e.target.style.borderColor = t.primary; }}
      onBlur={e =>  { e.target.style.borderColor = t.border;  }}
    />
  </div>
);

// ============================================================================
// COMPOSANT PRINCIPAL
// ============================================================================

const DevisManager = ({ onConvertirContrat, initialProspectId = null }) => {
  const t = useTheme();

  const [view,       setView]       = useState('list');  // 'list' | 'form'
  const [devisList,  setDevisList]  = useState([]);
  const [prospects,  setProspects]  = useState([]);
  const [tarifs,     setTarifs]     = useState({});
  const [loading,    setLoading]    = useState(true);
  const [saving,     setSaving]     = useState(false);
  const [error,      setError]      = useState('');
  const [success,    setSuccess]    = useState('');
  const [editItem,   setEditItem]   = useState(null);  // null = nouveau

  // Formulaire
  const [form, setForm] = useState({
    prospect_id:         '',
    type_licence:        'standard',
    discount_type:       'percent',
    discount_value:      0,
    validite_jours:      30,
    jour_paiement:       15,
    payment_method_id:   '',
    notes:               '',
  });
  const [prixBase,           setPrixBase]           = useState(0);
  const [montantFinal,       setMontantFinal]       = useState(0);
  const [paymentMethods,     setPaymentMethods]     = useState([]);
  const [loadingPayments,    setLoadingPayments]    = useState(false);
  const [extensionsDispo,    setExtensionsDispo]    = useState([]);
  const [licencePlans,       setLicencePlans]       = useState([]);
  const [extensionsChoisies, setExtensionsChoisies] = useState([]);
  const [addonEmployes,      setAddonEmployes]      = useState(0);
  const [addonUtilisateurs,  setAddonUtilisateurs]  = useState(0);

  // ── Charger ──
  useEffect(() => { loadAll(); }, []);

  // Pré-sélectionner le prospect si on arrive depuis ProspectionHub
  useEffect(() => {
    if (initialProspectId && prospects.length > 0) {
      handleNew();
      setForm(f => ({ ...f, prospect_id: String(initialProspectId) }));
      const { base, final } = calculerMontants('standard', 'percent', 0);
      setPrixBase(base); setMontantFinal(final);
    }
  }, [initialProspectId, prospects]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [dRes, pRes, tRes, pmRes, extRes, plansRes] = await Promise.all([
        superAdminApi.devis.getAll(),
        superAdminApi.prospects.getAll(),
        superAdminApi.danicedSettings.getTarifs(),
        superAdminApi.danicedSettings.getPaymentMethods(),
        superAdminApi.extensions.getAll(),
        superAdminApi.licences.getPlans(),
      ]);
      if (dRes.success) setDevisList(dRes.data || []);
      if (pRes.success) setProspects(pRes.data || []);
      if (tRes.success) {
        const map = {};
        (tRes.data || []).forEach(t => {
          map[t.type_licence] = {
            prix:                t.prix,
            label:               t.label,
            frais_mise_en_place: t.frais_mise_en_place || 0,
          };
        });
        setTarifs(map);
      }
      if (pmRes?.success) {
        const actifs = (pmRes.data || []).filter(m => m.actif == 1);
        setPaymentMethods(actifs);
        if (actifs.length > 0) {
          setForm(f => f.payment_method_id ? f : { ...f, payment_method_id: String(actifs[0].id) });
        }
      }
      if (extRes?.success)   setExtensionsDispo(extRes.data   || []);
      if (plansRes?.success) setLicencePlans(plansRes.plans   || {});
    } catch { setError('Erreur de chargement'); }
    finally { setLoading(false); }
  };

  // ── Calcul prix selon licence + réduction ──
  const calculerMontants = (typeLicence, discountType, discountValue, currentTarifs, exts = null, addEmp = null, addUser = null) => {
    const base = parseFloat((currentTarifs || tarifs)[typeLicence]?.prix || 0);
    const dv   = parseFloat(discountValue || 0);
    let licenceFinal = base;
    if (dv > 0) {
      licenceFinal = discountType === 'percent' ? base * (1 - dv / 100) : base - dv;
    }
    licenceFinal = Math.max(0, licenceFinal);

    const extsList   = exts    !== null ? exts    : extensionsChoisies;
    const empAddon   = addEmp  !== null ? addEmp  : addonEmployes;
    const userAddon  = addUser !== null ? addUser : addonUtilisateurs;

    const totalExts   = extsList.reduce((s, e) => s + parseFloat(e.prix_mensuel || 0), 0);
    const prixParEmp  = parseFloat((currentTarifs || tarifs)[typeLicence]?.prix_par_employe_sup  || 2500);
    const prixParUser = parseFloat((currentTarifs || tarifs)[typeLicence]?.prix_par_utilisateur_sup || 3500);
    const tranchesEmp = Math.ceil(empAddon / 10);
    const totalAddons = (tranchesEmp * prixParEmp) + (userAddon * prixParUser);

    return { base, final: licenceFinal + totalExts + totalAddons, licenceFinal, totalExts, totalAddons };
  };

  // Recalculer quand extensions ou addons changent
  const recalculer = (exts, addEmp, addUser) => {
    const { final } = calculerMontants(form.type_licence, form.discount_type, form.discount_value, null, exts, addEmp, addUser);
    setMontantFinal(final);
  };

  const setFormField = (key, val) => {
    setForm(f => {
      const updated = { ...f, [key]: val };
      if (['type_licence', 'discount_type', 'discount_value'].includes(key)) {
        const { base, final } = calculerMontants(
          updated.type_licence, updated.discount_type, updated.discount_value
        );
        setPrixBase(base);
        setMontantFinal(final);
      }
      return updated;
    });
  };

  // ── Nouveau devis ──
  const handleNew = () => {
    setEditItem(null);
    const firstPm = paymentMethods.length > 0 ? String(paymentMethods[0].id) : '';
    setForm({ prospect_id: '', type_licence: 'standard', discount_type: 'percent', discount_value: 0, validite_jours: 30, jour_paiement: 15, payment_method_id: firstPm, notes: '' });
    setExtensionsChoisies([]);
    setAddonEmployes(0);
    setAddonUtilisateurs(0);
    const { base, final } = calculerMontants('standard', 'percent', 0);
    setPrixBase(base); setMontantFinal(final);
    setError(''); setSuccess('');
    setView('form');
  };

  // ── Éditer un devis ──
  const handleEdit = (d) => {
    setEditItem(d);
    setForm({
      prospect_id:    d.prospect_id   || '',
      type_licence:   d.type_licence  || 'standard',
      discount_type:  d.discount_type || 'percent',
      discount_value: d.discount_value || 0,
      validite_jours: d.validite_jours || 30,
      jour_paiement:       d.jour_paiement       || 15,
      payment_method_id:   d.payment_method_id ? String(d.payment_method_id) : (paymentMethods.length > 0 ? String(paymentMethods[0].id) : ''),
      notes:               d.notes               || '',
    });
    setPrixBase(d.prix_base     || 0);
    setMontantFinal(d.montant_final || d.prix_base || 0);
    // Charger extensions + addons sauvegardés
    try { setExtensionsChoisies(JSON.parse(d.extensions_devis || '[]')); } catch { setExtensionsChoisies([]); }
    setAddonEmployes(parseInt(d.addon_employes) || 0);
    setAddonUtilisateurs(parseInt(d.addon_utilisateurs) || 0);
    setError(''); setSuccess('');
    setView('form');
  };

  // ── Sauvegarder ──
  const handleSave = async () => {
    if (!form.prospect_id) { setError('Choisissez un prospect'); return; }
    setSaving(true); setError(''); setSuccess('');
    try {
      let res;
      if (editItem) {
        res = await superAdminApi.devis.update(editItem.id, {
          discount_type:       form.discount_type,
          discount_value:      form.discount_value,
          validite_jours:      form.validite_jours,
          jour_paiement:       parseInt(form.jour_paiement) || 15,
          payment_method_id:   form.payment_method_id ? parseInt(form.payment_method_id) : null,
          addon_employes:      addonEmployes,
          addon_utilisateurs:  addonUtilisateurs,
          extensions_devis:    JSON.stringify(extensionsChoisies),
          notes:               form.notes,
        });
      } else {
        res = await superAdminApi.devis.create({
          prospect_id:    parseInt(form.prospect_id),
          type_licence:        form.type_licence,
          discount_type:       form.discount_type,
          discount_value:      form.discount_value,
          validite_jours:      form.validite_jours,
          jour_paiement:       parseInt(form.jour_paiement) || 15,
          payment_method_id:   form.payment_method_id ? parseInt(form.payment_method_id) : null,
          addon_employes:      addonEmployes,
          addon_utilisateurs:  addonUtilisateurs,
          extensions_devis:    JSON.stringify(extensionsChoisies),
          notes:               form.notes,
        });
      }
      if (!res.success) { setError(res.message || 'Erreur'); return; }
      setSuccess(editItem ? 'Devis mis à jour !' : `Devis ${res.data?.numero || ''} créé !`);
      await loadAll();
      setTimeout(() => { setSuccess(''); setView('list'); }, 1500);
    } catch { setError('Erreur réseau'); }
    finally { setSaving(false); }
  };

  // ── Actions rapides ──
  const handleMarkAccepted = async (id) => {
    try {
      await superAdminApi.devis.markAccepted(id);
      await loadAll();
    } catch { setError('Erreur'); }
  };

  const handleMarkRefused = async (id) => {
    try {
      await superAdminApi.devis.markRefused(id);
      await loadAll();
    } catch { setError('Erreur'); }
  };

  const handleWhatsApp = async (d) => {
    const prospect = prospects.find(p => p.id === d.prospect_id);
    const phone    = prospect?.telephone?.replace(/\D/g, '') || '';
    const token    = localStorage.getItem('superadmin_token');
    const pdfUrl   = `${API_URL}/sys-mgmt-${process.env.REACT_APP_ADMIN_ROUTE_HASH || '7f3a9c2e'}/devis/${d.id}/pdf?token=${token}`;
    const msg      = encodeURIComponent(`Bonjour ${prospect?.contact || prospect?.nom || ''},\n\nVeuillez trouver ci-joint notre devis ${d.numero} pour la licence HoraPilot ${d.plan_label || ''}.\n\nMontant mensuel : ${parseFloat(d.montant_final || 0).toLocaleString('fr-FR')} FCFA\n\nLien PDF : ${pdfUrl}\n\nCordialement,\nÉquipe HoraPilot`);
    window.open(`https://wa.me/${phone}?text=${msg}`, '_blank');
    await superAdminApi.devis.markWhatsappSent(d.id);
    await loadAll();
  };

  const handleConvertirContrat = async (d) => {
    try {
      // Créer le contrat depuis le devis (pré-rempli)
      const res = await superAdminApi.contrats.fromDevis(d.id);
      if (res.success && onConvertirContrat) {
        // Naviguer vers ContratManager avec le contrat ouvert
        onConvertirContrat({ contrat_id: res.contrat_id, numero: res.numero });
        await loadAll(); // Refresh pour mettre à jour statut devis
      } else if (!res.success) {
        setError(res.message || 'Erreur lors de la création du contrat');
      }
    } catch { setError('Erreur lors de la conversion en contrat'); }
  };

  const fmt = (n) => parseFloat(n || 0).toFixed(0).replace(/\B(?=(\d{3})+(?!\d))/g, ' ') + ' FCFA';

  if (loading) return (
    <div style={{ padding: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12 }}>
      <RefreshCw size={20} style={{ color: t.primary, animation: 'spin 1s linear infinite' }} />
      <span style={{ fontSize: 13, color: t.textMuted }}>Chargement…</span>
    </div>
  );

  // ============================================================================
  // VUE LISTE
  // ============================================================================
  if (view === 'list') return (
    <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* HEADER */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 style={{ fontSize: 18, fontWeight: 800, color: t.text, margin: '0 0 4px' }}>Devis</h1>
          <p style={{ fontSize: 13, color: t.textMuted, margin: 0 }}>{devisList.length} devis · {devisList.filter(d => d.statut === 'accepte').length} accepté(s)</p>
        </div>
        <button onClick={handleNew} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 20px',
          borderRadius: 10, background: t.primary, color: '#fff', border: 'none', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
          <Plus size={15} /> Nouveau devis
        </button>
      </div>

      {error && (
        <div style={{ padding: '10px 14px', borderRadius: 10, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', display: 'flex', gap: 8 }}>
          <AlertCircle size={14} style={{ color: '#ef4444' }} />
          <span style={{ fontSize: 12, color: '#ef4444' }}>{error}</span>
        </div>
      )}

      {/* STATS RAPIDES */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
        {[
          { label: 'Total',     value: devisList.length,                                           color: t.primary  },
          { label: 'Envoyés',   value: devisList.filter(d => d.statut === 'envoye').length,        color: '#3b82f6'  },
          { label: 'Acceptés',  value: devisList.filter(d => d.statut === 'accepte').length,       color: '#22c55e'  },
          { label: 'En attente',value: devisList.filter(d => ['brouillon','envoye'].includes(d.statut)).length, color: '#f59e0b' },
        ].map(stat => (
          <div key={stat.label} style={{ padding: '14px 16px', borderRadius: 12, background: t.bgCard, border: `1px solid ${t.border}` }}>
            <p style={{ fontSize: 22, fontWeight: 900, color: stat.color, margin: '0 0 2px' }}>{stat.value}</p>
            <p style={{ fontSize: 11, color: t.textMuted, margin: 0 }}>{stat.label}</p>
          </div>
        ))}
      </div>

      {/* LISTE */}
      {devisList.length === 0 ? (
        <div style={{ padding: 60, textAlign: 'center', color: t.textMuted }}>
          <FileText size={40} style={{ margin: '0 auto 12px', opacity: 0.3 }} />
          <p style={{ fontSize: 14, fontWeight: 600 }}>Aucun devis</p>
          <p style={{ fontSize: 12 }}>Créez votre premier devis pour un prospect</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {devisList.map(d => (
            <div key={d.id} style={{ padding: '14px 18px', borderRadius: 14, background: t.bgCard,
              border: `1px solid ${t.border}`, display: 'flex', alignItems: 'center', gap: 16 }}>

              {/* Icône statut */}
              <div style={{ width: 40, height: 40, borderRadius: 10, flexShrink: 0,
                background: STATUT_CONFIG[d.statut]?.bg || '#f3f4f6',
                display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <FileText size={18} style={{ color: STATUT_CONFIG[d.statut]?.color || '#9ca3af' }} />
              </div>

              {/* Infos */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 3 }}>
                  <p style={{ fontSize: 13, fontWeight: 800, color: t.text, margin: 0 }}>
                    {d.prospect_nom || 'Prospect inconnu'}
                  </p>
                  <StatBadge statut={d.statut} />
                  {d.plan_label && (
                    <span style={{ fontSize: 10, color: t.textMuted, padding: '2px 8px', borderRadius: 99, background: t.bgInput }}>
                      {d.plan_label}
                    </span>
                  )}
                  {d.whatsapp_sent === 1 && (
                    <span style={{ fontSize: 10, color: '#22c55e', padding: '2px 8px', borderRadius: 99, background: '#f0fdf4' }}>
                      📱 WhatsApp
                    </span>
                  )}
                </div>
                <p style={{ fontSize: 11, color: t.textMuted, margin: 0 }}>
                  {d.numero} · {fmt(d.montant_final)}/mois
                  {d.expires_at && ` · Expire le ${new Date(d.expires_at).toLocaleDateString('fr-FR')}`}
                  {d.prospect_contact && ` · ${d.prospect_contact}`}
                </p>
              </div>

              {/* ACTIONS */}
              <div style={{ display: 'flex', gap: 6, flexShrink: 0, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                {/* PDF */}
                <button onClick={() => superAdminApi.devis.getPdf(d.id)}
                  style={actionBtn(t.primarySoft, t.primary, t.primaryBorder)}>
                  <Eye size={12} /> PDF
                </button>

                {/* WhatsApp */}
                {['brouillon','envoye'].includes(d.statut) && (
                  <button onClick={() => handleWhatsApp(d)}
                    style={actionBtn('rgba(34,197,94,0.1)', '#22c55e', 'rgba(34,197,94,0.2)')}>
                    <MessageCircle size={12} /> WhatsApp
                  </button>
                )}

                {/* Accepté */}
                {['brouillon','envoye'].includes(d.statut) && (
                  <button onClick={() => handleMarkAccepted(d.id)}
                    style={actionBtn('rgba(34,197,94,0.1)', '#22c55e', 'rgba(34,197,94,0.2)')}>
                    <CheckCircle2 size={12} /> Accepté
                  </button>
                )}

                {/* Refusé */}
                {['brouillon','envoye'].includes(d.statut) && (
                  <button onClick={() => handleMarkRefused(d.id)}
                    style={actionBtn('rgba(239,68,68,0.1)', '#ef4444', 'rgba(239,68,68,0.2)')}>
                    <XCircle size={12} /> Refusé
                  </button>
                )}

                {/* Convertir en contrat */}
                {d.statut === 'accepte' && (
                  <button onClick={() => handleConvertirContrat(d)}
                    style={actionBtn('rgba(139,92,246,0.1)', '#8b5cf6', 'rgba(139,92,246,0.2)')}>
                    <ArrowRight size={12} /> Créer contrat
                  </button>
                )}

                {/* Éditer */}
                {['brouillon','envoye'].includes(d.statut) && (
                  <button onClick={() => handleEdit(d)}
                    style={actionBtn(t.bgInput, t.text, t.border)}>
                    <Edit3 size={12} /> Éditer
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      <style>{`@keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>
    </div>
  );

  // ============================================================================
  // VUE FORMULAIRE (Nouveau / Édition)
  // ============================================================================
  const selectedProspect = prospects.find(p => p.id === parseInt(form.prospect_id));

  return (
    <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* BARRE NAV */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button onClick={() => setView('list')} style={{ display: 'flex', alignItems: 'center', gap: 6,
            padding: '7px 12px', borderRadius: 8, background: t.bgInput, color: t.text,
            border: `1px solid ${t.border}`, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
            <ArrowLeft size={13} /> Retour
          </button>
          <h1 style={{ fontSize: 16, fontWeight: 800, color: t.text, margin: 0 }}>
            {editItem ? `Édition devis ${editItem.numero}` : 'Nouveau devis'}
          </h1>
        </div>
        <button onClick={handleSave} disabled={saving} style={{ display: 'flex', alignItems: 'center', gap: 8,
          padding: '10px 22px', borderRadius: 10, background: t.primary, color: '#fff', border: 'none',
          fontSize: 13, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1 }}>
          {saving
            ? <span style={{ width: 14, height: 14, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.8s linear infinite', display: 'inline-block' }} />
            : <Save size={14} />}
          {saving ? 'Enregistrement...' : 'Enregistrer'}
        </button>
      </div>

      {(error || success) && (
        <div style={{ padding: '10px 14px', borderRadius: 10, display: 'flex', gap: 8, alignItems: 'center',
          background: success ? 'rgba(34,197,94,0.08)' : 'rgba(239,68,68,0.08)',
          border: `1px solid ${success ? 'rgba(34,197,94,0.2)' : 'rgba(239,68,68,0.2)'}` }}>
          {success ? <CheckCircle2 size={14} style={{ color: '#22c55e' }} /> : <AlertCircle size={14} style={{ color: '#ef4444' }} />}
          <span style={{ fontSize: 12, color: success ? '#22c55e' : '#ef4444' }}>{success || error}</span>
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>

        {/* GAUCHE — Prospect + Licence */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Prospect */}
          <div style={{ padding: '18px 20px', borderRadius: 14, background: t.bgCard, border: `1px solid ${t.border}` }}>
            <p style={{ fontSize: 13, fontWeight: 800, color: t.text, margin: '0 0 12px', display: 'flex', alignItems: 'center', gap: 7 }}>
              <User size={14} style={{ color: t.primary }} /> Prospect
            </p>
            <label style={{ fontSize: 11, fontWeight: 700, color: t.textSecondary, display: 'block', marginBottom: 5 }}>
              {editItem ? 'Prospect lié' : 'Choisir un prospect *'}
            </label>
            {editItem ? (
              <div style={{ padding: '10px 12px', borderRadius: 8, background: t.bgInput, border: `1px solid ${t.border}`, fontSize: 12, color: t.text }}>
                {editItem.prospect_nom}
              </div>
            ) : (
              <select value={form.prospect_id} onChange={e => setFormField('prospect_id', e.target.value)}
                style={{ width: '100%', padding: '9px 10px', borderRadius: 8, background: t.bgInput,
                  border: `1px solid ${t.border}`, color: form.prospect_id ? t.text : t.textMuted, fontSize: 12, outline: 'none' }}>
                <option value="">— Choisir un prospect —</option>
                {prospects.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.nom}{p.contact ? ` · ${p.contact}` : ''}{p.ville ? ` (${p.ville})` : ''}
                  </option>
                ))}
              </select>
            )}

            {/* Fiche prospect */}
            {selectedProspect && (
              <div style={{ marginTop: 12, padding: '10px 12px', borderRadius: 8, background: t.primarySoft, border: `1px solid ${t.primaryBorder}` }}>
                <p style={{ fontSize: 11, fontWeight: 700, color: t.primary, margin: '0 0 5px' }}>{selectedProspect.nom}</p>
                {selectedProspect.contact   && <p style={{ fontSize: 11, color: t.textSecondary, margin: '0 0 2px' }}>👤 {selectedProspect.contact}</p>}
                {selectedProspect.telephone && <p style={{ fontSize: 11, color: t.textSecondary, margin: '0 0 2px' }}>📞 {selectedProspect.telephone}</p>}
                {selectedProspect.email     && <p style={{ fontSize: 11, color: t.textSecondary, margin: '0 0 2px' }}>✉️ {selectedProspect.email}</p>}
                {selectedProspect.ville     && <p style={{ fontSize: 11, color: t.textSecondary, margin: 0 }}>📍 {selectedProspect.ville}, {selectedProspect.pays}</p>}
              </div>
            )}
          </div>

          {/* Licence */}
          {!editItem && (
            <div style={{ padding: '18px 20px', borderRadius: 14, background: t.bgCard, border: `1px solid ${t.border}` }}>
              <p style={{ fontSize: 13, fontWeight: 800, color: t.text, margin: '0 0 12px', display: 'flex', alignItems: 'center', gap: 7 }}>
                <Building2 size={14} style={{ color: t.primary }} /> Licence proposée
              </p>
              <div style={{ display: 'flex', gap: 8, flexDirection: 'column' }}>
                {/* Sélecteur licence */}
                <div style={{ display: 'flex', gap: 8 }}>
                  {LICENCES.map(lic => {
                    const COLORS = { basique: '#3b82f6', standard: '#8b5cf6', premium: '#f59e0b' };
                    const c = COLORS[lic];
                    const selected = form.type_licence === lic;
                    return (
                      <button key={lic} onClick={() => setFormField('type_licence', lic)}
                        style={{ flex: 1, padding: '10px 6px', borderRadius: 10, cursor: 'pointer',
                          border: `2px solid ${selected ? c : t.border}`,
                          background: selected ? c + '15' : t.bgInput,
                          color: selected ? c : t.textMuted,
                          fontSize: 11, fontWeight: 800, textTransform: 'capitalize' }}>
                        <div style={{ fontSize: 9, marginBottom: 3, opacity: 0.7 }}>
                          {tarifs[lic]?.prix > 0 ? parseFloat(tarifs[lic].prix).toLocaleString('fr-FR') + ' FCFA' : '—'}
                        </div>
                        {lic}
                      </button>
                    );
                  })}
                </div>

                {/* Détails de la licence sélectionnée */}
                {(() => {
                  const plan = licencePlans[form.type_licence];
                  const tar  = tarifs[form.type_licence];
                  if (!plan && !tar) return null;
                  const COLORS = { basique: '#3b82f6', standard: '#8b5cf6', premium: '#f59e0b' };
                  const c = COLORS[form.type_licence] || t.primary;
                  const modules = plan?.modules || [];
                  return (
                    <div style={{ padding: '12px 14px', borderRadius: 10, background: c + '08', border: `1px solid ${c}25` }}>
                      {/* Limites */}
                      <div style={{ display: 'flex', gap: 16, marginBottom: modules.length > 0 ? 10 : 0 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ fontSize: 14 }}>👥</span>
                          <div>
                            <div style={{ fontSize: 11, fontWeight: 800, color: t.text }}>
                              {plan?.max_employes || parseInt(tar?.max_employes) || '—'} employés
                            </div>
                            <div style={{ fontSize: 9, color: t.textMuted }}>maximum</div>
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ fontSize: 14 }}>🔑</span>
                          <div>
                            <div style={{ fontSize: 11, fontWeight: 800, color: t.text }}>
                              {plan?.max_utilisateurs || parseInt(tar?.max_utilisateurs) || '—'} utilisateurs
                            </div>
                            <div style={{ fontSize: 9, color: t.textMuted }}>maximum</div>
                          </div>
                        </div>
                        {tar?.frais_mise_en_place > 0 && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto' }}>
                            <span style={{ fontSize: 14 }}>🔧</span>
                            <div>
                              <div style={{ fontSize: 11, fontWeight: 800, color: '#92400e' }}>
                                {parseFloat(tar.frais_mise_en_place).toLocaleString('fr-FR')} FCFA
                              </div>
                              <div style={{ fontSize: 9, color: t.textMuted }}>mise en place</div>
                            </div>
                          </div>
                        )}
                      </div>
                      {/* Modules inclus */}
                      {modules.length > 0 && (
                        <>
                          <div style={{ fontSize: 10, fontWeight: 700, color: t.textMuted, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                            Modules inclus
                          </div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                            {modules.map((m, i) => (
                              <span key={i} style={{
                                display: 'inline-flex', alignItems: 'center', gap: 4,
                                padding: '3px 8px', borderRadius: 99,
                                background: c + '15', border: `1px solid ${c}30`,
                                fontSize: 10, fontWeight: 600, color: c,
                              }}>
                                ✓ {typeof m === 'string' ? m : m.label}
                              </span>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  );
                })()}
              </div>
            </div>
          )}

          {/* Validité */}
          <div style={{ padding: '18px 20px', borderRadius: 14, background: t.bgCard, border: `1px solid ${t.border}` }}>
            <p style={{ fontSize: 13, fontWeight: 800, color: t.text, margin: '0 0 12px', display: 'flex', alignItems: 'center', gap: 7 }}>
              <Calendar size={14} style={{ color: t.primary }} /> Validité
            </p>
            <label style={{ fontSize: 11, fontWeight: 700, color: t.textSecondary, display: 'block', marginBottom: 5 }}>
              Durée de validité (jours)
            </label>
            <div style={{ display: 'flex', gap: 8 }}>
              {[15, 30, 45, 60].map(j => (
                <button key={j} onClick={() => setFormField('validite_jours', j)}
                  style={{ flex: 1, padding: '8px 0', borderRadius: 8, cursor: 'pointer',
                    border: `1px solid ${form.validite_jours === j ? t.primary : t.border}`,
                    background: form.validite_jours === j ? t.primarySoft : t.bgInput,
                    color: form.validite_jours === j ? t.primary : t.textMuted,
                    fontSize: 12, fontWeight: 700 }}>
                  {j}j
                </button>
              ))}
            </div>
            <p style={{ fontSize: 11, color: t.textMuted, marginTop: 8 }}>
              Expiration : {(() => { const d = new Date(); d.setDate(d.getDate() + parseInt(form.validite_jours)); return d.toLocaleDateString('fr-FR'); })()}
            </p>
          </div>
        </div>

        {/* DROITE — Conditions financières + Notes */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Financier */}
          <div style={{ padding: '18px 20px', borderRadius: 14, background: t.bgCard, border: `1px solid ${t.border}` }}>
            <p style={{ fontSize: 13, fontWeight: 800, color: t.text, margin: '0 0 12px', display: 'flex', alignItems: 'center', gap: 7 }}>
              <CreditCard size={14} style={{ color: t.primary }} /> Conditions financières
            </p>

            {/* Prix de base */}
            <div style={{ padding: '10px 12px', borderRadius: 8, background: t.bgInput, border: `1px solid ${t.border}`, marginBottom: 12 }}>
              <p style={{ fontSize: 10, color: t.textMuted, margin: '0 0 2px' }}>Prix de base ({form.type_licence})</p>
              <p style={{ fontSize: 16, fontWeight: 900, color: t.text, margin: 0 }}>
                {parseFloat(prixBase || tarifs[form.type_licence]?.prix || 0).toLocaleString('fr-FR')} FCFA
              </p>
            </div>

            {/* Réduction */}
            <label style={{ fontSize: 11, fontWeight: 700, color: t.textSecondary, display: 'block', marginBottom: 5 }}>
              Réduction (optionnel)
            </label>
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <select value={form.discount_type} onChange={e => setFormField('discount_type', e.target.value)}
                style={{ width: 80, padding: '8px 8px', borderRadius: 8, background: t.bgInput,
                  border: `1px solid ${t.border}`, color: t.text, fontSize: 12, outline: 'none' }}>
                <option value="percent">%</option>
                <option value="fixed">FCFA</option>
              </select>
              <input type="number" min={0} value={form.discount_value}
                onChange={e => setFormField('discount_value', parseFloat(e.target.value) || 0)}
                placeholder="0"
                style={{ flex: 1, padding: '8px 10px', borderRadius: 8, background: t.bgInput,
                  border: `1px solid ${t.border}`, color: t.text, fontSize: 12, outline: 'none' }} />
            </div>

            {/* Résumé */}
            {/* Récapitulatif détaillé */}
            <div style={{ padding: '12px 14px', borderRadius: 10, background: t.primarySoft, border: `1px solid ${t.primaryBorder}`, display: 'flex', flexDirection: 'column', gap: 5 }}>
              {/* Prix de base */}
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: t.textSecondary }}>
                <span>Licence {form.type_licence}</span>
                <span>{parseFloat(prixBase || tarifs[form.type_licence]?.prix || 0).toLocaleString('fr-FR')} FCFA</span>
              </div>
              {/* Réduction */}
              {form.discount_value > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#ef4444' }}>
                  <span>Réduction ({form.discount_type === 'percent' ? form.discount_value + '%' : parseFloat(form.discount_value).toLocaleString('fr-FR') + ' FCFA'})</span>
                  <span>- {form.discount_type === 'percent'
                    ? (parseFloat(prixBase) * form.discount_value / 100).toLocaleString('fr-FR')
                    : parseFloat(form.discount_value).toLocaleString('fr-FR')} FCFA</span>
                </div>
              )}
              {/* Extensions */}
              {extensionsChoisies.map(e => (
                <div key={e.flag_key} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#7c3aed' }}>
                  <span>+ {e.label}</span>
                  <span>{parseFloat(e.prix_mensuel || 0).toLocaleString('fr-FR')} FCFA</span>
                </div>
              ))}
              {/* Add-ons */}
              {addonEmployes > 0 && (() => {
                const tranches = Math.ceil(addonEmployes / 10);
                const prix = parseFloat(tarifs[form.type_licence]?.prix_par_employe_sup || 2500);
                return (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#d97706' }}>
                    <span>+ {addonEmployes} employés sup. ({tranches} tranche{tranches > 1 ? 's' : ''})</span>
                    <span>{(tranches * prix).toLocaleString('fr-FR')} FCFA</span>
                  </div>
                );
              })()}
              {addonUtilisateurs > 0 && (() => {
                const prix = parseFloat(tarifs[form.type_licence]?.prix_par_utilisateur_sup || 3500);
                return (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: '#d97706' }}>
                    <span>+ {addonUtilisateurs} utilisateur{addonUtilisateurs > 1 ? 's' : ''} sup.</span>
                    <span>{(addonUtilisateurs * prix).toLocaleString('fr-FR')} FCFA</span>
                  </div>
                );
              })()}
              {/* Séparateur + Total */}
              <div style={{ borderTop: `1px solid ${t.primaryBorder}`, paddingTop: 5, marginTop: 2 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 14, fontWeight: 900, color: t.primary }}>
                  <span>Total mensuel</span>
                  <span>{parseFloat(montantFinal || 0).toLocaleString('fr-FR')} FCFA</span>
                </div>
              </div>
              {/* Frais MEP */}
              {parseFloat(tarifs[form.type_licence]?.frais_mise_en_place || 0) > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: t.textSecondary, paddingTop: 4, borderTop: `1px dashed ${t.border}` }}>
                  <span>Frais de mise en place (one-shot)</span>
                  <span style={{ fontWeight: 700 }}>{parseFloat(tarifs[form.type_licence].frais_mise_en_place).toLocaleString('fr-FR')} FCFA</span>
                </div>
              )}
            </div>
          </div>

          {/* Extensions */}
          <div style={{ padding: '18px 20px', borderRadius: 14, background: t.bgCard, border: `1px solid ${t.border}` }}>
            <p style={{ fontSize: 13, fontWeight: 800, color: t.text, margin: '0 0 10px', display: 'flex', alignItems: 'center', gap: 7 }}>
              <Package size={14} style={{ color: '#7c3aed' }} />
              <span>Extensions</span>
              {extensionsChoisies.length > 0 && (
                <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 99, background: '#f5f3ff', color: '#7c3aed', fontWeight: 700 }}>
                  {extensionsChoisies.length} sélectionnée{extensionsChoisies.length > 1 ? 's' : ''}
                </span>
              )}
            </p>
            {extensionsDispo.length === 0 ? (
              <p style={{ fontSize: 12, color: t.textMuted, fontStyle: 'italic' }}>Aucune extension disponible</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                {extensionsDispo.map(ext => {
                  const choisie = extensionsChoisies.find(e => e.flag_key === ext.flag_key);
                  return (
                    <div key={ext.flag_key} style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '8px 12px', borderRadius: 9,
                      background: choisie ? '#f5f3ff' : t.bgInput,
                      border: `1px solid ${choisie ? '#c4b5fd' : t.border}`,
                      cursor: 'pointer',
                    }} onClick={() => {
                      let newExts;
                      if (choisie) {
                        newExts = extensionsChoisies.filter(e => e.flag_key !== ext.flag_key);
                      } else {
                        newExts = [...extensionsChoisies, { flag_key: ext.flag_key, label: ext.label, prix_mensuel: parseFloat(ext.prix_defaut || 0) }];
                      }
                      setExtensionsChoisies(newExts);
                      recalculer(newExts, addonEmployes, addonUtilisateurs);
                    }}>
                      <div style={{ width: 18, height: 18, borderRadius: 4, border: `2px solid ${choisie ? '#7c3aed' : t.border}`, background: choisie ? '#7c3aed' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        {choisie && <span style={{ color: 'white', fontSize: 11, fontWeight: 900 }}>✓</span>}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: choisie ? '#5b21b6' : t.text }}>{ext.label}</div>
                        {ext.description && <div style={{ fontSize: 10, color: t.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{ext.description}</div>}
                      </div>
                      {/* Prix éditable si sélectionnée */}
                      {choisie ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }} onClick={e => e.stopPropagation()}>
                          <input type="number" min={0} value={choisie.prix_mensuel}
                            onChange={e => {
                              const newExts = extensionsChoisies.map(ex =>
                                ex.flag_key === ext.flag_key ? { ...ex, prix_mensuel: parseFloat(e.target.value) || 0 } : ex
                              );
                              setExtensionsChoisies(newExts);
                              recalculer(newExts, addonEmployes, addonUtilisateurs);
                            }}
                            style={{ width: 80, padding: '4px 7px', borderRadius: 6, background: 'white', border: '1px solid #c4b5fd', color: '#5b21b6', fontSize: 12, fontWeight: 700, textAlign: 'right', outline: 'none' }}
                          />
                          <span style={{ fontSize: 10, color: '#7c3aed' }}>FCFA</span>
                        </div>
                      ) : (
                        <span style={{ fontSize: 11, color: t.textMuted, whiteSpace: 'nowrap' }}>
                          {parseFloat(ext.prix_defaut || 0).toLocaleString('fr-FR')} FCFA
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Add-ons employés + utilisateurs */}
          <div style={{ padding: '18px 20px', borderRadius: 14, background: t.bgCard, border: `1px solid ${t.border}` }}>
            <p style={{ fontSize: 13, fontWeight: 800, color: t.text, margin: '0 0 12px', display: 'flex', alignItems: 'center', gap: 7 }}>
              <Users size={14} style={{ color: '#d97706' }} /> Add-ons
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              {/* Add-on employés */}
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: t.textSecondary, display: 'block', marginBottom: 5 }}>
                  Employés supplémentaires
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input type="number" min={0} step={1} value={addonEmployes}
                    onChange={e => {
                      const v = Math.max(0, parseInt(e.target.value) || 0);
                      setAddonEmployes(v);
                      recalculer(extensionsChoisies, v, addonUtilisateurs);
                    }}
                    style={{ width: 80, padding: '8px 10px', borderRadius: 8, background: t.bgInput, border: `1px solid ${t.border}`, color: t.text, fontSize: 14, fontWeight: 700, textAlign: 'center', outline: 'none' }}
                    onFocus={e => { e.target.style.borderColor = '#f59e0b'; }}
                    onBlur={e =>  { e.target.style.borderColor = t.border;  }}
                  />
                  <span style={{ fontSize: 11, color: t.textMuted }}>employés</span>
                </div>
                {addonEmployes > 0 && (
                  <div style={{ marginTop: 5, fontSize: 10, color: '#d97706' }}>
                    {Math.ceil(addonEmployes / 10)} tranche{Math.ceil(addonEmployes / 10) > 1 ? 's' : ''} ×{' '}
                    {parseFloat(tarifs[form.type_licence]?.prix_par_employe_sup || 2500).toLocaleString('fr-FR')} FCFA
                    {' = '}{(Math.ceil(addonEmployes / 10) * parseFloat(tarifs[form.type_licence]?.prix_par_employe_sup || 2500)).toLocaleString('fr-FR')} FCFA/mois
                  </div>
                )}
              </div>
              {/* Add-on utilisateurs */}
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: t.textSecondary, display: 'block', marginBottom: 5 }}>
                  Utilisateurs supplémentaires
                </label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input type="number" min={0} step={1} value={addonUtilisateurs}
                    onChange={e => {
                      const v = Math.max(0, parseInt(e.target.value) || 0);
                      setAddonUtilisateurs(v);
                      recalculer(extensionsChoisies, addonEmployes, v);
                    }}
                    style={{ width: 80, padding: '8px 10px', borderRadius: 8, background: t.bgInput, border: `1px solid ${t.border}`, color: t.text, fontSize: 14, fontWeight: 700, textAlign: 'center', outline: 'none' }}
                    onFocus={e => { e.target.style.borderColor = '#f59e0b'; }}
                    onBlur={e =>  { e.target.style.borderColor = t.border;  }}
                  />
                  <span style={{ fontSize: 11, color: t.textMuted }}>utilisateurs</span>
                </div>
                {addonUtilisateurs > 0 && (
                  <div style={{ marginTop: 5, fontSize: 10, color: '#d97706' }}>
                    {addonUtilisateurs} × {parseFloat(tarifs[form.type_licence]?.prix_par_utilisateur_sup || 3500).toLocaleString('fr-FR')} FCFA
                    {' = '}{(addonUtilisateurs * parseFloat(tarifs[form.type_licence]?.prix_par_utilisateur_sup || 3500)).toLocaleString('fr-FR')} FCFA/mois
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Mode de paiement */}
          <div style={{ padding: '18px 20px', borderRadius: 14, background: t.bgCard, border: `1px solid ${t.border}` }}>
            <p style={{ fontSize: 13, fontWeight: 800, color: t.text, margin: '0 0 10px', display: 'flex', alignItems: 'center', gap: 7 }}>
              <CreditCard size={14} style={{ color: t.primary }} /> Mode de paiement
            </p>
            {paymentMethods.length === 0 ? (
              <div style={{ padding: '9px 12px', borderRadius: 8, background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.2)', fontSize: 12, color: '#b45309', display: 'flex', alignItems: 'center', gap: 8 }}>
                <AlertCircle size={13} style={{ flexShrink: 0 }} />
                Aucun mode configuré — ajoutez-en dans <strong style={{ marginLeft: 4 }}>Nos paramètres</strong>
              </div>
            ) : (
              <>
                <div style={{ position: 'relative' }}>
                  <select
                    value={form.payment_method_id}
                    onChange={e => setFormField('payment_method_id', e.target.value)}
                    style={{ width: '100%', padding: '9px 36px 9px 12px', borderRadius: 9, background: t.bgInput, border: `1px solid ${t.border}`, color: t.text, fontSize: 12, fontWeight: 500, appearance: 'none', cursor: 'pointer', outline: 'none' }}
                    onFocus={e => { e.target.style.borderColor = t.primary; }}
                    onBlur={e =>  { e.target.style.borderColor = t.border;  }}
                  >
                    <option value="">— Aucun mode sélectionné —</option>
                    {paymentMethods.map(m => (
                      <option key={m.id} value={m.id}>{m.libelle}</option>
                    ))}
                  </select>
                  <ChevronDown size={13} style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', color: t.textMuted, pointerEvents: 'none' }} />
                </div>
                {form.payment_method_id && (() => {
                  const pm = paymentMethods.find(m => String(m.id) === String(form.payment_method_id));
                  return pm ? (
                    <div style={{ marginTop: 8, padding: '9px 12px', borderRadius: 8, background: t.primarySofter, border: `1px solid ${t.primaryBorder}`, display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: t.primary }}>{pm.libelle}</span>
                      {pm.numero_rib    && <span style={{ fontSize: 12, fontWeight: 600, color: t.text }}>{pm.numero_rib}</span>}
                      {pm.instructions && <span style={{ fontSize: 11, color: t.textMuted, fontStyle: 'italic' }}>{pm.instructions}</span>}
                    </div>
                  ) : null;
                })()}
              </>
            )}
          </div>

          {/* Jour de paiement */}
          <div style={{ padding: '18px 20px', borderRadius: 14, background: t.bgCard, border: `1px solid ${t.border}` }}>
            <p style={{ fontSize: 13, fontWeight: 800, color: t.text, margin: '0 0 10px', display: 'flex', alignItems: 'center', gap: 7 }}>
              <Calendar size={14} style={{ color: t.primary }} /> Jour de paiement souhaité
            </p>
            <p style={{ fontSize: 11, color: t.textMuted, marginBottom: 10 }}>
              Jour du mois auquel le client souhaite être prélevé (1-31)
            </p>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input
                type="number" min={1} max={31}
                value={form.jour_paiement}
                onChange={e => setFormField('jour_paiement', Math.min(31, Math.max(1, parseInt(e.target.value) || 1)))}
                style={{ width: 80, padding: '9px 12px', borderRadius: 8, background: t.bgInput,
                  border: `1px solid ${t.border}`, color: t.text, fontSize: 16, fontWeight: 800,
                  textAlign: 'center', outline: 'none', boxSizing: 'border-box' }}
                onFocus={e => { e.target.style.borderColor = t.primary; }}
                onBlur={e  => { e.target.style.borderColor = t.border;  }}
              />
              <span style={{ fontSize: 12, color: t.textSecondary }}>du mois</span>
              <div style={{ marginLeft: 'auto', padding: '6px 12px', borderRadius: 8,
                background: t.primarySoft, border: `1px solid ${t.primaryBorder}` }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: t.primary }}>
                  📅 Paiement le {form.jour_paiement} de chaque mois
                </span>
              </div>
            </div>
          </div>

          {/* Notes */}
          <div style={{ padding: '18px 20px', borderRadius: 14, background: t.bgCard, border: `1px solid ${t.border}` }}>
            <p style={{ fontSize: 13, fontWeight: 800, color: t.text, margin: '0 0 10px' }}>Notes internes</p>
            <textarea value={form.notes} onChange={e => setFormField('notes', e.target.value)}
              rows={4} placeholder="Observations, contexte, conditions spéciales..."
              style={{ width: '100%', padding: '9px 12px', borderRadius: 8, background: t.bgInput,
                border: `1px solid ${t.border}`, color: t.text, fontSize: 12, resize: 'vertical',
                fontFamily: 'inherit', boxSizing: 'border-box', outline: 'none' }}
              onFocus={e => { e.target.style.borderColor = t.primary; }}
              onBlur={e =>  { e.target.style.borderColor = t.border;  }}
            />
          </div>
        </div>
      </div>

      <style>{`@keyframes spin { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }`}</style>
    </div>
  );
};

// Helper style bouton action
const actionBtn = (bg, color, border) => ({
  display: 'flex', alignItems: 'center', gap: 5,
  padding: '6px 10px', borderRadius: 7,
  background: bg, color, border: `1px solid ${border}`,
  fontSize: 11, fontWeight: 700, cursor: 'pointer',
  whiteSpace: 'nowrap',
});

export default DevisManager;
